import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from "react";
import { Link } from "./router";
import type { PublicConventionGuide } from "@verity-connect/contracts";

/**
 * The maps WVCEA drew, one per tab, opened the way a phone owner expects a picture
 * to open: pinch to zoom, drag to move, double-tap to fit.
 *
 * Nothing here is redrawn. An earlier version traced the campus by hand so rooms
 * could be tapped on the drawing, which meant the app could disagree with the
 * printed map somebody was holding. These are the printed maps.
 */
export type MapView = "campus" | "parking" | "anderson" | "servant-1" | "servant-3";

interface MapDefinition { view: MapView; tab: string; caption: string; description: string }

const CAMPUS: MapDefinition = {
  view: "campus",
  tab: "Campus",
  caption: "Appalachian Bible College campus",
  description: "Campus map with numbered buildings, both entrances, and the road layout.",
};

const VIEWS: readonly MapDefinition[] = [
  CAMPUS,
  {
    view: "parking",
    tab: "Parking",
    caption: "Where to park",
    description: "Parking lots 1 to 5 around Anderson Hall and the Servant Center, seen from above.",
  },
  {
    view: "anderson",
    tab: "Anderson Hall",
    caption: "Anderson Hall",
    description: "Anderson Hall floor plan: the Chapel, rooms 101, 115, 116 and 117, the lobby, both entrances and the restrooms.",
  },
  {
    view: "servant-1",
    tab: "Servant Center 1",
    caption: "Servant Center, first floor",
    description: "Servant Center first floor: classrooms SC101 and SC102, the coffee shop and the post office. SC101 is reached by the elevator from the third floor. SC102 is reached through SC101 or from the outside entrance.",
  },
  {
    view: "servant-3",
    tab: "Servant Center 3",
    caption: "Servant Center, third floor",
    description: "Servant Center third floor: classrooms SC301, SC302 and SC303, the Convention Vendors hall, offices, restrooms, elevator and stairs.",
  },
];

/**
 * Read off the color key printed on WVCEA's own sheet, so a swatch beside a room
 * number is the same color as the block on the map.
 *
 * Sampled from the map files themselves rather than typed from a swatch list, so the
 * two cannot drift apart. On the Servant Center sheets WVCEA redrew, some of these
 * repeat: SC301 and SC302 are the same green, SC101 and SC102 the same grey-blue.
 * That is the maps as drawn, and `uniqueOnMap` below is what keeps the app from
 * claiming a color picks out one room when it does not.
 */
const ROOM_COLORS: Record<string, string> = {
  CHAPEL: "#d10778",
  "AN 101": "#1d337b",
  "AN 115": "#d01f26",
  "AN 116": "#009eca",
  "AN 117": "#f7e908",
  "SC 101": "#8588a4",
  "SC 102": "#8588a4",
  "SC 301": "#899e63",
  "SC 302": "#899e63",
  "SC 303": "#b16858",
};

/**
 * The words printed inside the room on the map. Where the color no longer tells two
 * rooms apart, this does — she reads it off the drawing the same way she reads a
 * door number off a door.
 */
const MAP_LABELS: Record<string, string> = {
  CHAPEL: "Chapel",
  "AN 101": "Anderson Hall 101",
  "AN 115": "Anderson Hall 115",
  "AN 116": "Anderson Hall 116",
  "AN 117": "Anderson Hall 117",
  "SC 101": "Classroom SC101",
  "SC 102": "Classroom SC102",
  "SC 301": "Classroom SC301",
  "SC 302": "Classroom SC302",
  "SC 303": "Classroom SC303",
};

const ROOMS_BY_VIEW: Record<MapView, readonly string[]> = {
  campus: [],
  parking: [],
  anderson: ["CHAPEL", "AN 101", "AN 115", "AN 116", "AN 117"],
  "servant-1": ["SC 101", "SC 102"],
  "servant-3": ["SC 301", "SC 302", "SC 303"],
};

const COLOR_NAMES: Record<string, string> = {
  CHAPEL: "pink",
  "AN 101": "dark blue",
  "AN 115": "red",
  "AN 116": "light blue",
  "AN 117": "yellow",
  "SC 101": "grey-blue",
  "SC 102": "grey-blue",
  "SC 301": "green",
  "SC 302": "green",
  "SC 303": "red",
};

/**
 * Whether naming this room's color would pick it out, or send her to the wrong door.
 *
 * "SC 301 is the green room on this map" was true of the old Servant Center sheet and
 * is a coin toss on the new one, where SC302 is the same green. A color said out loud
 * has to name one block or it is worse than saying nothing.
 */
function uniqueOnMap(code: string, view: MapView): boolean {
  return !ROOMS_BY_VIEW[view].some((other) => other !== code && ROOM_COLORS[other] === ROOM_COLORS[code]);
}

export function parseMapView(value: string | null): MapView {
  return VIEWS.some((entry) => entry.view === value) ? value as MapView : "campus";
}

export function mapViewForRoom(code: string): MapView {
  if (code === "CHAPEL" || code.startsWith("AN ")) return "anderson";
  if (code.startsWith("SC 1")) return "servant-1";
  if (code.startsWith("SC 3")) return "servant-3";
  return "campus";
}

/**
 * Bumped whenever a map picture is redrawn, and mirrored in the service worker's
 * precache list.
 *
 * The pictures keep their filenames, and `_headers` gives `/maps/*` a week of
 * `max-age`. Without something in the address to tell the two apart, a phone that
 * opened the map in the last week answers its own request from its own cache, and
 * WVCEA's new drawing never arrives — not over the network, and not into the service
 * worker either, whose own fetch is served by that same cache. Changing the address
 * is what makes it a different picture as far as every cache between here and the
 * screen is concerned. `/assets/*` gets this for free from the hash in its filenames.
 */
const MAP_RELEASE = "2";

const MIN_SCALE = 1;
const MAX_SCALE = 6;

/**
 * Where the reader came from, and what to call it.
 *
 * The Map is a tab of its own, so it draws no back link — a tab root has nowhere above
 * it. But a room link is not the tab root: she was reading the room directory, or a
 * session, and the map answers one question she asked on the way past. Landing there with
 * no route back made her hunt for the section she had been in a second earlier.
 *
 * The sender says where it sent her, in `from`. Nothing is guessed: the same
 * `/map?room=AN101` arrives from the directory under More and from a session under
 * Schedule, and a link that guessed would send half of them to a page they had never
 * seen. No `from` means no link, which is what a plain `/map` deserves.
 *
 * Only a path on this site, and never `//host/path`, which reads as a path right up until
 * a URL parser sees it. A back link taken from the address bar is a link a stranger can
 * write.
 */
function cameFrom(value: string | null): { href: string; label: string } | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  const path = value.split("?")[0] ?? "";
  if (path === "/more/rooms") return { href: value, label: "Room directory" };
  if (path === "/more/event") return { href: value, label: "Event details" };
  if (path === "/more") return { href: value, label: "More" };
  if (path.startsWith("/schedule")) return { href: value, label: "Back to the session" };
  if (path.startsWith("/my-wvcea")) return { href: value, label: "Back to the session" };
  return null;
}

export function ConventionMapPage({ guide, search }: { guide: PublicConventionGuide; search: string }) {
  const params = new URLSearchParams(search);
  const roomCode = params.get("room");
  const back = cameFrom(params.get("from"));
  const view = roomCode ? mapViewForRoom(roomCode) : parseMapView(params.get("view"));
  const current = VIEWS.find((entry) => entry.view === view) ?? CAMPUS;
  const rooms = ROOMS_BY_VIEW[view];
  const highlighted = roomCode && rooms.includes(roomCode) ? roomCode : null;

  useEffect(() => {
    document.title = `Map · ${guide.event.name}`;
  }, [guide.event.name]);

  // On a phone the tabs wrap, so every one of them is already on screen and there is
  // nothing to scroll. Where the row still runs off the side — a narrow window on a
  // desktop — arriving from a room link can select a tab that is off the edge, which
  // reads as no tab being selected at all.
  const switcher = useRef<HTMLElement>(null);
  useEffect(() => {
    const row = switcher.current;
    if (!row || row.scrollWidth <= row.clientWidth) return;
    row.querySelector('[aria-current="page"]')?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [view]);

  return (
    <div className="map-page">
      {back && <Link className="back-link subpage-back" href={back.href}>← {back.label}</Link>}
      <div className="map-heading">
        <h1 data-page-heading tabIndex={-1}>Map</h1>
        <p>{current.caption}</p>
      </div>
      <nav className="map-switcher" aria-label="Which map" ref={switcher}>
        {VIEWS.map((entry) => (
          <Link key={entry.view} href={`/map?view=${entry.view}`} aria-current={entry.view === view ? "page" : undefined}>{entry.tab}</Link>
        ))}
      </nav>
      {highlighted && (
        <p className="map-room-hint" role="status">
          {uniqueOnMap(highlighted, view) ? (
            <>
              <b style={{ background: ROOM_COLORS[highlighted] }} aria-hidden="true" />
              {highlighted} is the {COLOR_NAMES[highlighted]} room on this map.
            </>
          ) : (
            <>{highlighted} is marked “{MAP_LABELS[highlighted]}” on this map.</>
          )}
        </p>
      )}
      <MapViewer key={view} src={`/maps/${view}.jpg?v=${MAP_RELEASE}`} description={current.description} />
      {rooms.length > 0 && (
        <section className="map-rooms" aria-label={`Rooms on the ${current.caption} map`}>
          <h2>Rooms on this map</h2>
          <div className="map-room-list">
            {rooms.map((code) => {
              const room = guide.rooms.find((candidate) => candidate.code === code);
              if (!room) return null;
              return (
                <Link key={code} className={`map-room-chip${code === highlighted ? " is-highlighted" : ""}`} href={`/schedule?room=${encodeURIComponent(room.id)}`}>
                  {/* A swatch is a promise that this color finds the room. Where two rooms
                      share one, it is a promise the map cannot keep, so it is not made. */}
                  {uniqueOnMap(code, view) && <b style={{ background: ROOM_COLORS[code] }} aria-hidden="true" />}
                  <span>{code}</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

interface Point { x: number; y: number }
interface Frame { scale: number; x: number; y: number }

/** The first two fingers down, or nothing, so a pinch never reads half a gesture. */
function twoPointers(active: Map<number, Point>): [Point, Point] | null {
  const [first, second] = [...active.values()];
  return first && second ? [first, second] : null;
}

const FIT: Frame = { scale: 1, x: 0, y: 0 };

/**
 * Pinch, drag and double-tap, written against pointer events so one set of code
 * serves a finger, a mouse and a trackpad. The picture is never allowed to be
 * dragged past its own edges, because on a phone that leaves a blank screen and
 * no clue how to get back.
 */
function MapViewer({ src, description }: { src: string; description: string }) {
  const [frame, setFrame] = useState<Frame>(FIT);
  const [ratio, setRatio] = useState<number | null>(null);
  const stage = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ distance: number; scale: number; center: { x: number; y: number } } | null>(null);
  const lastTap = useRef(0);

  /** Keeps the picture covering the frame whatever the scale is. */
  const settle = useCallback((next: Frame): Frame => {
    const box = stage.current?.getBoundingClientRect();
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next.scale));
    if (!box) return { scale, x: 0, y: 0 };
    const slackX = (box.width * (scale - 1)) / 2;
    const slackY = (box.height * (scale - 1)) / 2;
    return {
      scale,
      x: Math.min(slackX, Math.max(-slackX, next.x)),
      y: Math.min(slackY, Math.max(-slackY, next.y)),
    };
  }, []);

  /** Zooms about a point so what was under the finger stays under the finger. */
  const zoomAbout = useCallback((factor: number, point: { x: number; y: number }) => {
    setFrame((previous) => {
      const box = stage.current?.getBoundingClientRect();
      if (!box) return settle({ ...previous, scale: previous.scale * factor });
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, previous.scale * factor));
      const applied = scale / previous.scale;
      const fromCenter = { x: point.x - box.left - box.width / 2, y: point.y - box.top - box.height / 2 };
      return settle({
        scale,
        x: fromCenter.x - (fromCenter.x - previous.x) * applied,
        y: fromCenter.y - (fromCenter.y - previous.y) * applied,
      });
    });
  }, [settle]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const pair = twoPointers(pointers.current);
    if (pair) {
      const [first, second] = pair;
      gesture.current = {
        distance: Math.hypot(first.x - second.x, first.y - second.y),
        scale: frame.scale,
        center: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 },
      };
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const pair = twoPointers(pointers.current);
    if (pair && gesture.current) {
      const [first, second] = pair;
      const distance = Math.hypot(first.x - second.x, first.y - second.y);
      const start = gesture.current;
      const wanted = start.scale * (distance / start.distance);
      setFrame((held) => {
        const box = stage.current?.getBoundingClientRect();
        if (!box) return settle({ ...held, scale: wanted });
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, wanted));
        const applied = scale / held.scale;
        const fromCenter = { x: start.center.x - box.left - box.width / 2, y: start.center.y - box.top - box.height / 2 };
        return settle({
          scale,
          x: fromCenter.x - (fromCenter.x - held.x) * applied,
          y: fromCenter.y - (fromCenter.y - held.y) * applied,
        });
      });
      return;
    }

    // One finger only moves the picture once it is bigger than the frame; before
    // that there is nowhere to move it to, and swallowing the drag would stop the
    // page itself from scrolling.
    if (frame.scale <= MIN_SCALE) return;
    setFrame((held) => settle({ ...held, x: held.x + (event.clientX - previous.x), y: held.y + (event.clientY - previous.y) }));
  };

  const endPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  };

  const onDoubleTap = (event: ReactPointerEvent<HTMLDivElement>) => {
    const now = event.timeStamp;
    const quick = now - lastTap.current < 320;
    lastTap.current = now;
    if (!quick) return;
    if (frame.scale > MIN_SCALE + 0.01) setFrame(FIT);
    else zoomAbout(2.5, { x: event.clientX, y: event.clientY });
  };

  const onWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    if (!event.ctrlKey && Math.abs(event.deltaY) < 2) return;
    event.preventDefault();
    zoomAbout(event.deltaY < 0 ? 1.12 : 1 / 1.12, { x: event.clientX, y: event.clientY });
  };

  const step = (factor: number) => {
    const box = stage.current?.getBoundingClientRect();
    if (!box) return;
    zoomAbout(factor, { x: box.left + box.width / 2, y: box.top + box.height / 2 });
  };

  const zoomed = frame.scale > MIN_SCALE + 0.01;

  return (
    <div className="map-viewer">
      {/* The frame takes the shape of the picture inside it. The maps are all
          different shapes, and a fixed frame would either crop one or leave grey
          bands above and below another. */}
      <div
        className={`map-stage${zoomed ? " is-zoomed" : ""}`}
        style={ratio ? { aspectRatio: `${ratio}` } : undefined}
        ref={stage}
        onPointerDown={(event) => { onPointerDown(event); onDoubleTap(event); }}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={onWheel}
      >
        {/* Zooming grows the box the picture is drawn into rather than stretching
            what was already drawn. A stretched picture goes soft exactly when
            somebody has zoomed in to read a room number. Moving is a separate
            step, because shifting a picture sideways costs nothing. */}
        <div
          className="map-pan"
          style={{
            width: `${frame.scale * 100}%`,
            height: `${frame.scale * 100}%`,
            left: `${(1 - frame.scale) * 50}%`,
            top: `${(1 - frame.scale) * 50}%`,
            transform: `translate3d(${frame.x}px, ${frame.y}px, 0)`,
          }}
        >
          <img
            src={src}
            alt={description}
            draggable={false}
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              if (naturalWidth > 0 && naturalHeight > 0) setRatio(naturalWidth / naturalHeight);
            }}
          />
        </div>
        {ratio === null && <p className="map-loading">Opening the map…</p>}
      </div>
      <div className="map-controls">
        <div className="map-zoom" role="group" aria-label="Zoom">
          <button type="button" onClick={() => step(1 / 1.4)} disabled={!zoomed} aria-label="Zoom out">−</button>
          <button type="button" onClick={() => step(1.4)} disabled={frame.scale >= MAX_SCALE - 0.01} aria-label="Zoom in">+</button>
          <button type="button" className="map-fit" onClick={() => setFrame(FIT)} disabled={!zoomed}>Fit</button>
        </div>
      </div>
    </div>
  );
}
